import { ReactElement, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import {
  LC_PAGE_COMPETENCY_CHAR_LIMIT,
  LC_PAGE_SUBJECT_CHAR_LIMIT,
} from '@lessonPlanner/constants';
import { Language } from '@lessonPlanner/enums';

import {
  SelectField,
  TextAreaField,
  TextField,
} from '../components/form-fields';
import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import DynamicLabelGenerateButton from '../components/molecules/DynamicLabelGenerateButton';
import { GradeLevelDropdown } from '../components/Select';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import { defaultCreditCost } from '../constants/credits';
import { CROSS_SUBJECT_DESCRIPTION } from '../constants/crossSubjectExtension';
import { useGeneratePause } from '../context/generate/provider';
import { PageKey, SubjectExtensionsProperties } from '../enums/pageKey';
import useAutosizeTextArea from '../hooks/useAutoSizeTextArea';
import { useScrollToError } from '../hooks/useScrollToError';
import SidebarLayout from '../layouts/SidebarLayout';
import SubjectExtensionSchema from '../schemas/SubjectExtensionSchema';
import { subjectExtensionsProperties } from '../types/page';
import processInput from '../utils/formInput';

export default function SubjectExtensions(): JSX.Element {
  const defaultValues = {
    subject:
      typeof window !== 'undefined'
        ? localStorage.getItem(SubjectExtensionsProperties.subject) || ''
        : '',
    gradeLevel:
      typeof window !== 'undefined'
        ? localStorage.getItem(SubjectExtensionsProperties.gradeLevel) || ''
        : '',
    objective:
      typeof window !== 'undefined'
        ? localStorage.getItem(SubjectExtensionsProperties.objective) || ''
        : '',
    language:
      typeof window !== 'undefined'
        ? localStorage.getItem(SubjectExtensionsProperties.language) ||
          'English'
        : 'English',
  };

  const subjectExtensionsForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(SubjectExtensionSchema),
  });

  const {
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = subjectExtensionsForm;

  const subject = watch('subject');
  const gradeLevel = watch('gradeLevel');
  const objective = watch('objective');
  const language = watch('language');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(textAreaRef.current, objective);

  const formContents = {
    [SubjectExtensionsProperties.subject]: subject,
    [SubjectExtensionsProperties.gradeLevel]: gradeLevel,
    [SubjectExtensionsProperties.objective]: objective,
    [SubjectExtensionsProperties.language]: language,
  };

  async function triggerValidation(fieldName) {
    await trigger(fieldName);
  }

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  async function customFormValidator() {
    await trigger();
  }

  const banner = (
    <GenericBanner
      content={CROSS_SUBJECT_DESCRIPTION}
      title="Learning Within and Across the Curriculum"
    />
  );

  const hasErrors = Object.entries(errors).length > 0;
  useScrollToError(errors);

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();
  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.subjectExtention];

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  return (
    <MainContent
      banner={banner}
      formContents={formContents as subjectExtensionsProperties}
      pageKey={PageKey.subjectExtention}
      setIsLoading={setIsLoading}
    >
      <TextField
        className="col-span-full"
        errorMessage={!!errors.subject && 'Please enter the Subject'}
        id="subject"
        isMaxLengthReached={subject?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT}
        label="Subject"
        maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
        name="subject"
        placeholder={'Input the subject'}
        type="subject"
        value={subject}
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            'subject',
            subject?.trim() || '',
          );
        }}
        onChange={(e) =>
          processInput(
            updateFieldValue,
            triggerValidation,
            'subject',
            e.target.value,
          )
        }
      />
      <GradeLevelDropdown
        error={!!errors.gradeLevel}
        errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
        value={gradeLevel}
        onChange={(value) => updateFieldValue('gradeLevel', value)}
        onBlur={() => {
          triggerValidation('gradeLevel');
        }}
      />
      <TextAreaField
        showSubLabel
        className="col-span-full"
        error={!!errors.objective}
        errorMessage={!!errors.objective && 'Please enter the Objectives'}
        id="objective"
        isMaxLengthReached={objective?.length >= LC_PAGE_COMPETENCY_CHAR_LIMIT}
        label="Objectives"
        maxLength={LC_PAGE_COMPETENCY_CHAR_LIMIT}
        name="objective"
        placeholder="Input the objectives"
        rows={2}
        textAreaRef={textAreaRef}
        type="objective"
        value={objective}
        subLabel={
          'What objectives should be connected in harmony with other subjects?'
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            'objective',
            objective?.trim() || '',
          );
        }}
        onChange={(e) =>
          processInput(
            updateFieldValue,
            triggerValidation,
            'objective',
            e.target.value,
          )
        }
      />
      <SelectField
        className="col-span-full"
        error={!!errors.language}
        errorMessage={errors.language?.message}
        id="language"
        label="Translation"
        name="language"
        value={language}
        onChange={(value) => updateFieldValue('language', value)}
        onBlur={() => {
          triggerValidation('language');
        }}
      >
        <option value={Language.ENGLISH}>{Language.ENGLISH}</option>
        <option value={Language.FILIPINO}>{Language.FILIPINO}</option>
      </SelectField>
      <div className="col-span-full">
        <div className="flex flex-col">
          <DynamicLabelGenerateButton
            isRegularDefault
            buttonText={buttonText}
            creditCost={defaultCreditCost}
            handleOnCLick={customFormValidator}
            isDisabled={isGenerateButtonDisabled}
          />
        </div>
      </div>
    </MainContent>
  );
}

SubjectExtensions.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
